// Import frontend Sogni generation (direct SDK for logged-in users)
import { generateImagesDirect } from './sogni-generate.js';

// Sign-in gate shared with the LLM tools
import { requireSignedInClient } from './require-sign-in.js';

// Import studio settings (mature-content filter override)
import { studioSettings, ttsPlayback } from '../state/settings.svelte.js';
import { DEFAULT_TTS_VOICE, requestSpeech, requestTranscription } from './voice-api.js';
import { approveImageRequest } from './generation-billing.svelte.js';
import { recordGenerationEvent } from '../state/generation-run.svelte.js';
import { recoverGenerationFailure } from '../state/credit-recovery.svelte.js';
import { saveGenerationAttempt } from '../state/session-persistence.svelte.js';

// LLM functions run in the browser on the signed-in user's own Sogni account
// (see ../llm/). Signed out, they open the sign-in modal and throw — the server
// has no LLM path to fall back to.
export { refinePrompts } from '../llm/refine.js';
export { getWizardQuestion, getWizardQuestionsBatch } from '../llm/wizard.js';
export { describeImage } from '../llm/structured.js';
export {
  decomposePrompt,
  facetComponent,
  generateWizardCombinations,
  diversifyPrompt,
  diversifyPrompts
} from '../llm/prompt-tools.js';

export const GENERATION_SIGN_IN_MESSAGE = 'Sign in to generate images — they render on your own Sogni account.';

/**
 * Generate images on the signed-in user's own Sogni account (browser SDK).
 * Signed out: opens the sign-in modal and throws AUTH_REQUIRED. There is no
 * server/guest render path — the Vibe server never spends its credentials.
 */
export async function generateImages(prompts, onProgress, options = {}) {
  requireSignedInClient(GENERATION_SIGN_IN_MESSAGE);

  let request;
  try {
    request = await approveImageRequest(prompts, {
      ...options,
      network: options.network || studioSettings.network,
      modelOverrides: options.modelOverrides ?? studioSettings.modelOverrides,
      preset: options.preset || studioSettings.renderPreset,
      disableNSFWFilter: !!studioSettings.matureContentEnabled
    });
    return await generateImagesDirect(request, message => {
      recordGenerationEvent(message);
      onProgress?.(message);
      if (message.type === 'request:started') return saveGenerationAttempt(message.request);
    });
  } catch (error) {
    recoverGenerationFailure(error, { kind: 'images', request: error.request || request, quote: error.quote });
    throw error;
  }
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Health check (still uses REST)
export async function checkHealth(timeoutMs = 10000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${API_URL}/health`, { signal: controller.signal });
    if (!response.ok) {
      throw new Error(`Health check failed with status ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Health check timed out');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

// Generate all combinations from faceted components (REST endpoint)
export async function generateCombinations(components, facets) {
  const response = await fetch(`${API_URL}/api/generate-combinations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ components, facets })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to generate combinations');
  }

  return response.json();
}

// Exponential backoff for retries
export async function withRetry(fn, maxAttempts = 3) {
  const delays = [1000, 2000, 4000];

  for (let i = 0; i < maxAttempts; i++) {
    try {
      return await fn();
    } catch (error) {
      if (i === maxAttempts - 1) throw error;

      // Don't retry on client errors (4xx)
      if (error.message.includes('400') || error.message.includes('401')) {
        throw error;
      }

      await new Promise(resolve => setTimeout(resolve, delays[i]));
    }
  }
}

// Format duration for display
export function formatDuration(ms) {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

// Format time in 12-hour format
export function formatTime12Hour(timestamp) {
  return new Date(timestamp).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

// Download image helper
export async function downloadImage(imageUrl, filename = null) {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `sogni-vibe-${Date.now()}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
}

// Text-to-speech using Sogni Voice API
const VOICE_API_KEY = import.meta.env.VITE_VOICE_API_KEY;
export async function textToSpeech(text, voice = DEFAULT_TTS_VOICE, { signal } = {}) {
  try {
    return await requestSpeech(text, { voice, apiKey: VOICE_API_KEY, signal });
  } catch (error) {
    if (!signal?.aborted) {
      ttsPlayback.error = 'Audio guidance could not load. You can keep typing, retry audio, or mute it.';
    }
    throw error;
  }
}

// Transcribe audio to text using Sogni Voice API
export async function transcribeAudio(audioBlob) {
  return requestTranscription(audioBlob, { apiKey: VOICE_API_KEY });
}

// Copy to clipboard helper
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Copy to clipboard failed:', error);

    // Fallback for older browsers
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand('copy');
      return true;
    } catch (e) {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }
}
